import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { pushEvent } from "../analytics/gtm";
import { isOnboardingMobileDevice } from "./device";
import { onboardingStepsForDevice, type OnboardingStep, type OnboardingStepId } from "./steps";
import {
  readOnboarding,
  startOnboardingPersisted,
  numberedOnboardingSteps,
  numberedStepProgress,
  stepIndex,
  writeOnboarding,
} from "./storage";

type OnboardingContextValue = {
  active: boolean;
  step: OnboardingStep | null;
  stepId: OnboardingStepId | null;
  stepNumber: number;
  totalSteps: number;
  focusDate: string | null;
  mobile: boolean;
  start: () => void;
  skip: () => void;
  next: () => void;
  goTo: (id: OnboardingStepId) => void;
  setFocusDate: (iso: string | null) => void;
  /** Advance only if currently on this step */
  completeStep: (id: OnboardingStepId) => void;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

function advanceId(current: OnboardingStepId, mobile: boolean): OnboardingStepId | null {
  const steps = onboardingStepsForDevice(mobile);
  const i = stepIndex(current, mobile);
  if (i < 0 || i >= steps.length - 1) return null;
  return steps[i + 1].id;
}

function stepAnalyticsParams(id: OnboardingStepId, mobile: boolean) {
  const steps = onboardingStepsForDevice(mobile);
  const step = steps.find((s) => s.id === id);
  return {
    step_id: id,
    step_number: numberedStepProgress(id, mobile),
    step_title: step?.title ?? id,
    total_steps: numberedOnboardingSteps(mobile).length,
    unnumbered: Boolean(step?.unnumbered),
  };
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const initial = readOnboarding();
  const [wizardDisabledOnMobile, setWizardDisabledOnMobile] = useState(
    isOnboardingMobileDevice,
  );
  const [status, setStatus] = useState<"idle" | "active" | "done">(
    initial?.status === "active" ? "active" : "idle",
  );
  const [mobile, setMobile] = useState(initial?.mobile ?? isOnboardingMobileDevice());
  const steps = useMemo(() => onboardingStepsForDevice(mobile), [mobile]);
  const [stepId, setStepId] = useState<OnboardingStepId>(
    initial?.status === "active" ? initial.stepId : steps[0].id,
  );
  const [focusDate, setFocusDateState] = useState<string | null>(
    initial?.status === "active" ? (initial.focusDate ?? null) : null,
  );

  const persist = useCallback(
    (next: {
      status: "active" | "done" | "skipped";
      stepId: OnboardingStepId;
      focusDate?: string | null;
      mobile?: boolean;
    }) => {
      writeOnboarding({
        status: next.status,
        stepId: next.stepId,
        focusDate: next.focusDate === undefined ? focusDate : next.focusDate,
        mobile: next.mobile ?? mobile,
      });
    },
    [focusDate, mobile],
  );

  useEffect(() => {
    const query = window.matchMedia("(max-width:600px)");
    const update = () => setWizardDisabledOnMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // Fire when each wizard step is shown (including resume mid-tour).
  useEffect(() => {
    if (status !== "active" || wizardDisabledOnMobile) return;
    pushEvent("onboarding_step", stepAnalyticsParams(stepId, mobile));
  }, [status, stepId, mobile, wizardDisabledOnMobile]);

  const start = useCallback(() => {
    const deviceMobile = isOnboardingMobileDevice();
    if (deviceMobile) {
      setStatus("idle");
      setFocusDateState(null);
      return;
    }
    setMobile(deviceMobile);
    startOnboardingPersisted(deviceMobile);
    const firstStep = onboardingStepsForDevice(deviceMobile)[0].id;
    setStatus("active");
    setStepId(firstStep);
    setFocusDateState(null);
  }, []);

  const skip = useCallback(() => {
    pushEvent("onboarding_skip", stepAnalyticsParams(stepId, mobile));
    persist({ status: "skipped", stepId });
    setStatus("done");
  }, [persist, stepId, mobile]);

  const finish = useCallback(() => {
    pushEvent("onboarding_complete", stepAnalyticsParams(stepId, mobile));
    persist({ status: "done", stepId });
    setStatus("done");
  }, [persist, stepId, mobile]);

  const goTo = useCallback(
    (id: OnboardingStepId) => {
      setStepId(id);
      persist({ status: "active", stepId: id });
    },
    [persist],
  );

  const next = useCallback(() => {
    const nxt = advanceId(stepId, mobile);
    if (!nxt) {
      finish();
      return;
    }
    goTo(nxt);
  }, [finish, goTo, stepId, mobile]);

  const completeStep = useCallback(
    (id: OnboardingStepId) => {
      if (status !== "active" || stepId !== id) return;
      const nxt = advanceId(id, mobile);
      if (!nxt) {
        finish();
        return;
      }
      goTo(nxt);
    },
    [finish, goTo, status, stepId, mobile],
  );

  const setFocusDate = useCallback(
    (iso: string | null) => {
      setFocusDateState(iso);
      if (status === "active") {
        persist({ status: "active", stepId, focusDate: iso });
      }
    },
    [persist, status, stepId],
  );

  const onboardingActive = status === "active" && !wizardDisabledOnMobile;
  const step = onboardingActive ? (steps.find((s) => s.id === stepId) ?? null) : null;

  const value = useMemo<OnboardingContextValue>(
    () => ({
      active: onboardingActive,
      step,
      stepId: onboardingActive ? stepId : null,
      stepNumber: numberedStepProgress(stepId, mobile),
      totalSteps: numberedOnboardingSteps(mobile).length,
      focusDate,
      mobile,
      start,
      skip,
      next,
      goTo,
      setFocusDate,
      completeStep,
    }),
    [onboardingActive, step, stepId, focusDate, mobile, start, skip, next, goTo, setFocusDate, completeStep],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}

/** Safe hook when provider might be absent (tests / public pages). */
export function useOnboardingOptional(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}
